'use strict'

import UploadService from '../services/uploadservice.coffee'
import UploadListService from '../services/uploadlistservice.coffee'
import Chain from '../helpers/chain.coffee'
import iOSImageRenamer from './ios-imagerenamer.coffee'
import FileProcessor from './fileprocessor.coffee'
import Thumbnails from './thumbnails.coffee'
import Compression from './compression.coffee'
import Encryption from './encryption.coffee'
import ZipHelper from '../services/ziphelper.coffee'
import CRC from './crc.coffee'
import FeatureCheck from '../helpers/featurecheck'
import Transfer from '../models/transfer.coffee'
import FileItem from '../models/fileitem.coffee'
import FolderItem from '../models/folderItem.coffee'
import { $log, $q } from '../helpers/angular-migration-helpers'

# Global settings
defaultCompressionLevel = 6 # Best trade-off between speed and size
minOutstandingBlockBytes = 500000 # We won't upload blocking data if there is less than 500KB pending in the queue
maxParallelUploads = 10 # We don't want more than 10 parallel uploads

getRelativePath = (file) ->
  # Obtain the relative path without trailing slash
  relativePath = file.relativePath || file.webkitRelativePath || file.name
  if relativePath
    stripLength = relativePath.length
    while (
      relativePath.length > 1 and
      (relativePath[stripLength - 1] == '/' or
        relativePath[stripLength - 1] == '\\')
    )
      --stripLength
    if stripLength != relativePath.length
      relativePath = relativePath.substring 0, stripLength
  relativePath

class Upload extends Transfer
  class UploadFileItem extends FileItem
    # Run the specified actions
    runActions = (promise, list, condition, action, initialValue) ->
      for item in list
        if condition item
          ((i) ->
            intAction = (ret) -> $q.when action i, ret
            promise = if promise
              promise.then((ret) -> intAction ret)
            else
              intAction initialValue
          ) item
      promise

    constructor: (id, blob, transfer) ->
      # Call base
      super(
        id
        blob.targetName || blob.name
        blob.size
        blob.type
        blob.lastModifiedDate
      )

      # Save the blob itself
      @blob = blob

      # Update state
      @isPending = true
      @state = 'initializing'
      @transfer = transfer

    requiresUpload: ->
      true

    preProcess: (preProcessActions, folder) ->
      chain = new Chain()

      # Keep variable that we should process the record
      continueProcessing = true

      # Check if there are pre-processing actions
      if preProcessActions?.length
        # Pre-processing started
        @state = 'preprocessing'

        # Run all preprocessor actions before actually adding the file
        chain.chainActions preProcessActions, (processor) =>
          if continueProcessing and processor.preProcessFile
            continueProcessing = processor.preProcessFile this, folder

      chain.chainAction =>
        # File is ready for upload
        @state = if continueProcessing
          'scheduledForUpload'
        else
          'skipped'

      chain.chain

    processFileBlocks: ->
      processActions = @processActions || @transfer._processActions
      return true for processAction in processActions when (
        processAction.processFileBlocks? this
      )
      false

    process: ->
      # The 'process' method runs all the process actions that are defined for the file. It returns a promise
      # that will be fulfilled when file-processing has completed. If it returns null, then processing is done
      # (or didn't need to occur).
      promise = null
      processActions = @processActions || @transfer._processActions
      if processActions?.length
        start = Date.now()
        #$log.log "#{@name} processing starts"

        # Processing started
        @state = 'processing'

        # Remember the original size
        originalSize = @size

        # Execute all slice processing actions
        promise = runActions(
          promise
          processActions
          (p) -> p.processFile
          (p) => p.processFile this
        )
        promise = $q
          .when promise
          .then =>
            # Update the total queue size with the updated blob size when all processing has finished
            newSize = @blob.size
            if originalSize != newSize
              # Update the total upload size
              @transfer._totalUploadSize += newSize - originalSize

            # Processing finished
            $log.log "#{@name} processing finished (#{Date.now() - start}ms)"

      # Return the promise
      promise

    getNextSlice: (chunked) ->
      # Determine the chunk-size for this
      chunkSize = if chunked
        UploadService.getChunkSize() / 2
      else
        @blob.size

      # Determine the current chunk and total number of chunks
      @currentChunk ?= 0
      fetchedChunk = @currentChunk++

      # Check if we need to slice the blob
      if @blob.size > chunkSize or @fetchedBytes
        # Determine the actual chunk-size for this chunk
        bytesLeftToSend = @blob.size - @fetchedBytes
        chunkSize = bytesLeftToSend if chunkSize > bytesLeftToSend

        # Obtain the blob that needs to be sent
        blob = @blob.slice @fetchedBytes, @fetchedBytes + chunkSize
      else
        chunkSize = @blob.size
        blob = @blob

      # Set the number of fetched bytes
      @fetchedBytes += chunkSize

      # Determine the total amount of chunks
      if chunked
        @totalChunks =
          @currentChunk + Math.ceil (@blob.size - @fetchedBytes) / chunkSize
      else
        @totalChunks = 1

      # Execute the slice processing
      processActions = @processActions || @transfer._processActions
      if processActions?.length
        # Set state to 'uploading' with slice processing. This prevents that the next
        # chunk might be sent, before the current slice has been processed.
        @state = 'uploadingSliceProcessing'

        # Execute all slice processing actions
        promise = runActions(
          null
          processActions
          (p) -> p.processSlice
          (p, b) => p.processSlice this, b, fetchedChunk
          blob
        )

        # Resolve the blob
        if promise
          dfd = $q.defer()
          promise.then (b) =>
            # Set state to 'uploading' again
            @state = 'uploading'

            # Update the total upload size
            @transfer._totalUploadSize += b.size - chunkSize

            # Resolve
            dfd.resolve
              blob: b
              chunkNr: fetchedChunk
              originalSize: chunkSize
              processedSize: b.size
          return dfd.promise
        else
          # Set state to 'uploading' again
          @state = 'uploading'

      # Resolve immediately
      blob: blob
      chunkNr: fetchedChunk
      originalSize: chunkSize
      processedSize: blob.size

    sliceUploaded: (chunkSize) ->
      @uploadedBytes += chunkSize
      if @uploadedBytes == @size
        # In-memory blobs are removed to save memory
        delete @blob if !(@blob instanceof File)

        # File has been uploaded
        @isPending = false
        @state = 'uploaded'

        # Update the folder's pending count
        @folder?._updatePendingCount -1, -@size

    addLinkedFile: (linkedFile, linkType) ->
      @linkedFiles = [] if !@linkedFiles
      @linkedFiles.push linkedFile
      linkedFile.originalFile = this
      linkedFile.linkType = linkType

    removeLinkedFile: (linkedFile) ->
      index = @linkedFiles.indexOf linkedFile
      if index >= 0
        @linkedFiles.splice index, 1
        delete linkedFile.originalFile
        delete linkedFile.linkType

    getKey: ->
      # Obtain the base key
      key = super()

      # Add link-type
      key += @linkType if @linkType

      # Return the key
      key

  # Process helper functions
  _isFileArray = (files) ->
    Array.isArray(files) or
    Object.prototype.toString.call(files) == '[object FileList]'

  _getNextSlice = (fileItem, chunked) ->
    # If the file is scheduled for upload, then it needs to be processed first
    if fileItem.state is 'scheduledForUpload'
      # Perform file processing
      promise = fileItem.process()

      # File is ready for uploading after it has been processed
      promise = $q
        .when promise
        .then ->
          # File can be uploaded
          fileItem.uploadedBytes = fileItem.fetchedBytes = 0
          fileItem.state = 'uploading'

          # Obtain the next slice from the file
          fileItem.getNextSlice chunked
    else
      # Get the next slice
      fileItem.getNextSlice chunked

  _findNormalUploadFile: ->
    # Find the first file that can be uploaded
    index = @_normalUploadIndex
    while index < @_uploadFiles.length
      # Obtain file information
      fileItem = @_uploadFiles[index]

      # Return the first original file
      if !fileItem.originalFile
        switch fileItem.state
          when 'scheduledForUpload'
            # If the file is scheduled for upload, then we start uploading it right-away
            return fileItem

          when 'uploaded'
            # If all files before this file have been uploaded to, then
            # we can update the upload start index
            @_normalUploadIndex = index + 1

          when 'uploading'
            # If we haven't fetched all data yet, then we can send more chunk of this entry
            return fileItem if fileItem.fetchedBytes < fileItem.blob.size

            # We have already fetched all data (but it hasn't been send yet). We don't need to send any more
            # chunks of this file and we can move on to the next file.

          else
            $log.error 'This should not happen.'
      else
        # Increment the normal upload index if the upload index is set to a sidecar file
        ++@_normalUploadIndex if @_normalUploadIndex == index

      # Attempt next file
      ++index

    # no file can be found
    null

  _uploadNormalFiles: (progressCallback) ->
    dfd = $q.defer()

    partSentCallback = =>
      # Increment the number of sent parts
      ++@_sendParts
      @_sendConfirmUpload()

    # Upload all folders
    for id, folder of @folders when not folder.zipOffset?
      zipHeader = ZipHelper.getFolderZipHeader folder, false
      folder.zipOffset = UploadService.getZipOffset @_uploadToken
      UploadService.uploadRawPart(
        @_uploadToken
        zipHeader.getArrayBuffer()
        null
        progressCallback
      )

    do uploader = =>
      # Find the file that needs to be uploaded
      uploadFileItem = @_findNormalUploadFile()

      # Obtain the token
      token = @_uploadToken

      # If there are no files to upload anymore, then we're done and we can fulfill the promise
      if !uploadFileItem
        encryptionBits = if @options.aesPassPhrase
          Encryption.aesSize
        else
          0
        zipOffset = UploadService.getZipOffset token
        centralDirectory = ZipHelper.getCentralDirectory(
          encryptionBits
          @_uploadFiles
          @folders
          zipOffset
        )
        $q
          .when(
            UploadService.uploadRawPart(
              token
              centralDirectory.getArrayBuffer()
              partSentCallback
              progressCallback
            )
          )
          .then ->
            UploadService.finishUploadParts(
              token
              partSentCallback
              progressCallback
            )
          .then -> dfd.resolve()
        return

      # Abort if the token is aborted
      if token.isAborted()
        dfd.reject 'aborted'
        return

      # Abort if we're being cancelled
      if @_isCancelled
        dfd.reject 'canceled'
        return

      # Obtain the next slice from this file
      $q
        .when _getNextSlice uploadFileItem, true
        .then ({
          blob: blob
          chunkNr: chunkNr
          originalSize: originalSize
          processedSize: processedSize
        }) =>
          promise = null
          if chunkNr == 0
            uploadFileItem.delayCrc =
              (!uploadFileItem.crc? and !uploadFileItem.isEncrypted) or
              (uploadFileItem.compressionLevel and
                !uploadFileItem.compressedSize?)
            uploadFileItem.zipOffset = UploadService.getZipOffset token
            encryptionBits = if @options.aesPassPhrase
              Encryption.aesSize
            else
              0
            zipHeader = ZipHelper.getFileZipHeader(
              encryptionBits
              uploadFileItem
              false
            )
            promise = UploadService.uploadPart(
              token
              uploadFileItem
              zipHeader.getArrayBuffer()
              chunkNr
              partSentCallback
              progressCallback
            )
            uploadFileItem.zipDataOffset = UploadService.getZipOffset token

          # Upload the actual part
          promise = $q
            .when promise
            .then ->
              sliceSize = blob.size
              if sliceSize > 0
                sliceCallback = -> uploadFileItem.sliceUploaded originalSize
                UploadService.uploadPart(
                  token
                  uploadFileItem
                  blob
                  chunkNr
                  partSentCallback
                  progressCallback
                  sliceCallback
                )

          # Send ZIP trailer (only required when using encryption or CRC is delayed)
          if (
            chunkNr == uploadFileItem.totalChunks - 1 and
            (uploadFileItem.isEncrypted or uploadFileItem.delayCrc)
          )
            promise = $q
              .when promise
              .then ->
                # Determine ZIP-trailer
                zipTrailer = ZipHelper.getFileZipTrailer(
                  encryptionBits
                  uploadFileItem
                )
                UploadService.uploadPart(
                  token
                  uploadFileItem
                  zipTrailer.getArrayBuffer()
                  chunkNr
                  partSentCallback
                  progressCallback
                )

          # Send the rest of the files
          $q
            .when promise
            .then -> uploader()

    # Return the promise (will be fulfilled when all normal files have been uploaded)
    dfd.promise

  _findSidecarUploadFile: ->
    # We should find the files that can be uploaded. Sidecars can be uploaded out-of-order,
    # but we prefer to send them in the proper order. Non-sidecars should always be uploaded
    # in the proper order, because they are stored inside the ZIP-file.
    enoughInPipeline =
      @_uploadToken.getOutstandingBytes() >= minOutstandingBlockBytes
    processingFiles = 0

    # Find the first file that can be uploaded
    index = @_sidecarUploadIndex
    while index < @_uploadFiles.length
      # Obtain file information
      fileItem = @_uploadFiles[index]

      # If the file is a sidecar, then they can be processed out-of-order.
      if fileItem.originalFile
        switch fileItem.state
          when 'scheduledForUpload'
            if enoughInPipeline
              # If there is enough data in the pipeline and we are already processing another item that might
              # block the queue, then we bail out. We assume that there is enough data in the upload queue, so
              # it won't drain, before the blocking operation completes.
              return null if processingFiles > 0

              # There is enough data in the pipeline, but there is no item that is currently blocking. We can
              # try to send the next item. If it blocks, then we assume there is enough data in the upload queue,
              # so it won't drain.
              return fileItem

            # There isn't enough data in the pipeline, so we rather don't send a file that might block. This
            # might drain the upload queue
            if not fileItem.processFileBlocks()
              return fileItem

            # There isn't enough data in the pipeline, but this entry might block. This could drain the upload
            # queue (what we want to prevent, because it will waste upload bandwidth). For that reason, we will
            # try the next file

          when 'uploaded'
            # If all files before this file have been uploaded to, then
            # we can update the upload start index
            @_sidecarUploadIndex = index + 1 if @_sidecarUploadIndex is index

          when 'uploading'
            # If we haven't fetched all data yet, then we can send more chunk of this entry
            return fileItem if fileItem.fetchedBytes < fileItem.blob.size

            # We have already fetched all data (but it hasn't been send yet). We don't need to send any more
            # chunks of this file and we can move on to the next file.

          when 'processing'
            # This file is currently processing. We don't want to process multiple files at once,
            # because it might take too much CPU or memory power.
            ++processingFiles
      else
        # Increment the sidecar upload index if the upload index is set to a normal file
        ++@_sidecarUploadIndex if @_sidecarUploadIndex == index

      # Check next item
      ++index

    # There is no suitable file to be found. During the end of an upload this might happen, because all
    # non-processable files (i.e. the raw images) have already been sent, but the thumbnails haven't been
    # send yet.
    #
    # In this case we will allow sending a file that might block (and potentially drain the upload queue),
    # but only if no other file is already busy processing

    # If there is already a file busy processing, then we abort
    return null if processingFiles > 0

    # No file is processing, so we will try to find the first file that is ready for upload
    index = @_sidecarUploadIndex
    while index < @_uploadFiles.length
      # Obtain file information
      fileItem = @_uploadFiles[index]

      # Check if this file is ready for uploading
      return fileItem if (
        fileItem.originalFile and fileItem.state is 'scheduledForUpload'
      )

      # Check next item
      ++index

    # No items found
    null

  _uploadSidecarFiles: (progressCallback) ->
    dfd = $q.defer()

    # Count the number of parallel sidecar uploads
    parallelSidecarUploads = 0

    # Upload the sidecars
    do uploader = =>
      # Abort if the token is aborted
      if @_uploadToken.isAborted()
        dfd.reject 'aborted'
        return

      # Abort if we're being cancelled
      if @_isCancelled
        dfd.reject 'canceled'
        return

      # If there are already to many uploads pending, then we'll abort immediately
      if @_uploadToken.getParallelUploads() >= maxParallelUploads
        return

      # Find the file that needs to be uploaded
      uploadFileItem = @_findSidecarUploadFile()
      if uploadFileItem
        # Increment the number of sidecar uploads
        ++parallelSidecarUploads

        # Obtain the next slice from this file
        $q
          .when _getNextSlice uploadFileItem, false
          .then ({
            blob: blob
            originalSize: originalSize
            processedSize: processedSize
          }) =>
            promise = UploadService.uploadSidecar(
              @_uploadToken
              uploadFileItem
              blob
              progressCallback
            )
            promise.then =>
              # Slice uploaded
              uploadFileItem.sliceUploaded processedSize

              # Decrement the number of sidecar uploads
              --parallelSidecarUploads

              # Send confirmation (if conditions are met)
              @_sendConfirmUpload()

              # Upload the rest of the pending files
              uploader()
      else
        # If there are no items left to upload, then we finished uploading the sidecars
        dfd.resolve() if (
          !parallelSidecarUploads and
          @_sidecarUploadIndex == @_uploadFiles.length
        )

    # Return the promise (will be fulfilled when all normal files have been uploaded)
    dfd.promise

  uploadPendingFiles: ->
    # Start time of this upload
    @_lastTransferStart = Date.now()

    # Handle the progress
    progressCallback = (deltaBytes) =>
      @uploadedBytes += deltaBytes
      @_calculateTransferRate false

    # Start uploading the normal and sidecar files
    promiseNormal = @_uploadNormalFiles progressCallback
    promiseSidecar = @_uploadSidecarFiles progressCallback

    # Wait until both the normal files and sidecars have been uploaded
    promise = $q.all [promiseNormal, promiseSidecar]
    promise.catch =>
      # Finished uploading
      $log.error 'Uploading aborted.'

      # Delete the upload
      @deleteUpload false

    # Confirm the upload (will return immediately if it has already been confirmed)
    promise = promise.then =>
      @_sendConfirmUpload()

    # Finish the upload
    promise = promise.then =>
      # Finish the upload
      @_finishUpload().then =>

        # Raise progress
        @raiseProgressEvent()

        # Increment the total transfer time
        @_totalTransferTime += Date.now() - @_lastTransferStart

        # Cancel the upload from the list
        UploadListService.finishUpload this

        # Finished uploading
        $log.log 'Uploading finished'

  _addAction: (action) ->
    # Determine the action string
    actionString = action.getString()

    # Obtain the type of the action
    actionType = actionString.split(':')[0]

    # We need to check if the action already exists. If so, then we need to check
    # if it's compatible with the current action.
    for existingAction in @_processActions
      # Obtain the action string of the existing action
      existingActionString = existingAction.getString()

      # Check if it's the same action
      existingActionType = existingActionString.split(':')[0]
      if actionType is existingActionType
        # Check if the arguments are compatible
        if actionString isnt existingActionString
          $log.error(
            "Incompatible action string '#{actionString}' and '#{existingActionString}'"
          )

        # Already listed
        isListed = true

    # If the action isn't listed, then we add the action
    @_processActions.push action if !isListed

  _sendConfirmUpload: ->
    # Return the existing promise if the confirm is already (being) sent
    return @_confirmPromise if @_confirmPromise

    # Cannot confirm if upload is cancelled
    return null if @_isCancelled

    # Cannot confirm if no part has been sent yet
    return null if !@_sendParts

    # Scan through the upload files to check if all conditions are met to send the confirmation
    for file in @_uploadFiles
      # Check if we all sidecars have been sent
      if file.requiredForConfirmation and file.state != 'uploaded'
        return null

    # Make sure at least 75% has been sent
    if @uploadedBytes / @getTotalUploadSize() < 0.75
      return false

    # Make sure we don't upload longer than 1 hour. If the download is very fast, then
    # we don't have an ETA yet which is fine as well
    if @secondsLeft and @secondsLeft > 3600
      return false

    # Resolve the confirmation immediately
    #
    # This is a left-over of the old confirmation method. I have kept it here
    # if we want to introduce confirmation later again.
    dfd = $q.defer()
    dfd.resolve()

    # Remember the confirm promise and return it
    @_confirmPromise = dfd.promise

  _calculateTransferRate: (forceUpdate) ->
    # Determine current values
    currentTimeStamp = Date.now()

    # Determine duration since last call
    durationSinceStart = currentTimeStamp - @_lastTransferStart

    # Determine the average transfer-rate up to now
    @avgTransferRate =
      @uploadedBytes / ((@_totalTransferTime + durationSinceStart) / 1000)

    # Determine the number of seconds that we need to upload the rest of the data
    @secondsLeft = Math.round(
      (@getTotalUploadSize() - @uploadedBytes) / @avgTransferRate
    )

    # Check if we need to update the transfer-rate
    if (
      (forceUpdate ||
        !@lastTransferRateUpdate ||
        currentTimeStamp - @lastTransferRateUpdate > @transferRateInterval) and
      @progressCallback
    )
      @lastTransferRateUpdate = currentTimeStamp
      @progressCallback(
        @uploadedBytes
        @getTotalUploadSize()
        @getTotalUploadFiles()
        @avgTransferRate
        @secondsLeft
      )

  _finishUpload: ->
    UploadService.finishUpload @_uploadToken, @avgTransferRate
      .then =>
        # The upload has finished
        @_isUploadFinished = true

        # Clear the upload-queue and related counters
        @_uploadFiles = []
        @_totalUploadSize = 0
        @_uploadIndex = 0
        @uploadedBytes = 0

        # The file isn't a new transfer anymore
        @_hasProcessedConfirmation = false
        delete @isNewTransfer

        # Remove any files that should be deleted, because they have been replaced
        if @_pendingRemoveItems?.length
          UploadService.removeItems(
            @_uploadToken
            this
            []
            @_pendingRemoveItems
          ).then =>
            # No files need to be deleted anymore
            delete @_pendingRemoveItems
      .then =>
        # Clear the upload token
        @_uploadToken = null

  _addToParentFolder: (uploadFileItem, baseFolder) ->
    if relativePath = getRelativePath uploadFileItem.blob
      parentFolderPath = relativePath.split('/')[0..-2].join '/'
      parentFolderPath = "#{baseFolder.getCompletePath()}/#{parentFolderPath}" if (
        baseFolder? and baseFolder isnt @rootFolder
      )
      parentFolder = @getFolderByPath parentFolderPath

    # Add the item to the folder
    (parentFolder || baseFolder || @rootFolder).addItem uploadFileItem

  _addFolder: (folder, baseFolder) ->
    uploadFolderItem = new FolderItem @nextFileId++, folder.name
    uploadFolderItem.lastModifiedDate = folder.lastModifiedDate
    uploadFolderItem.blob = folder
    uploadFolderItem.state = 'readyForUpload'
    @folders[uploadFolderItem.id] = uploadFolderItem

    # Get the parent folder
    @_addToParentFolder uploadFolderItem, baseFolder

  _removeUploadFileItem: (uploadFileItem) ->
    # Remove the file from the folder
    folder = uploadFileItem.folder
    if folder
      folder.removeItem uploadFileItem

  _getZipFileOverhead: (uploadFileItem) ->
    # Overhead per file is:
    # - Zip header (30 bytes)
    # - Optional encryption data (11 [extra-data] + 16 [salt] + 2 [pw_ver] + 10 [hash] bytes)
    # - Data descriptor (16 bytes)
    # -
    # - Central directory header (46 bytes)
    # - 2x full path (zip header and central directory)
    extraBytes = if @options.aesPassPhrase then 39 else 0
    extraBytes += 32 if uploadFileItem.size >= 4 * 1024 * 1024 * 1024
    30 +
      extraBytes +
      16 +
      46 +
      extraBytes +
      2 * uploadFileItem.getCompletePath().length

  constructor: (serverTransfer) ->
    # Call base
    super serverTransfer

    # No parts have been sent yet
    @_sendParts = 0

    # Progress indicator
    @uploadedBytes = 0 # Number of bytes of chunks that have been uploaded completely

    # Transfer rate indicators
    @transferRateInterval = 500 # Interval of transfer rate calculation (in ms)
    @avgTransferRate = 0 # Overall transfer rate (bytes/sec)
    @lastTransferRateUpdate = 0 # Last transfer-rate update

    # Returned data of the server
    @shareUrl = null # Short URL (link, facebook, twitter)

    # Progress callback
    @progressCallback = null # Callback that is invoked when progress is updated

    # Indicators
    @confirmedUpload = false # Indicator whether the upload has already been confirmed
    @_isUploadFinished = false # Indicator whether the upload has been finished

    # Internal variables for the controller
    @_uploadToken = null
    @_startingUploadPromise = null
    @_isUploading = false
    @_lastTransferRateTotalBytes = 0
    @_totalTransferTime = 0

    # The upload index specifies the file that is currently being uploaded
    @_normalUploadIndex = 0
    @_sidecarUploadIndex = 0
    @_uploadFiles = []
    @_totalUploadSize = 22 + 27

    # Create the preprocessors (encryption is done later, because we need to know the key)
    @_processActions = []
    @_compression = null

    # Extend the current options
    @options.aesPassPhrase = null # Encrypted transfer
    @options.enableCompression = FeatureCheck.canCompress() # Enable compression

    # If not called with a server transfer, then we need to initialize some values ourselves
    if !serverTransfer
      @createdAt = new Date()
      @transfer = 'upload'
      @isNewTransfer = true

      # Set the default email options
      @options.email =
        recipients: [] # List of recipients (at least one)
        deleteAfterDownload: false # Download transfer when all recipients have downloaded the file

      # Register as active upload
      UploadService.registerUpload this
    else
      # Server transfers are always confirmed
      @confirmedUpload = true

      # Update the email options
      @options.email ?= {}
      @options.email.recipients =
        serverTransfer.to?.map((recipient) ->
          recipient.downloaded = recipient.downloaded if recipient.downloaded
          recipient.downloadedPartially = recipient.downloadedPartially if (
            recipient.downloadedPartially
          )
          recipient
        ) || []
      @options.isEncrypted = !!serverTransfer.encrypted

      # Obtain the shortUrl
      @shortUrl = serverTransfer.id
      @shareUrl = serverTransfer.shareUrl

  update: (serverTransfer) ->
    # Call base
    super serverTransfer

    # Returned data of the server
    @shareUrl = serverTransfer.shareUrl

  isEncrypted: ->
    !!(@options.aesPassPhrase || @options.isEncrypted)

  getState: ->
    # If the upload hasn't been finished
    if @_isUploadFinished
      return 'finished'

    # If there are no files added, then we are still in the initialized state
    if @_uploadFiles.length == 0
      return 'initialized'

    # TODO: Check if the upload has been queued

    # Check if we are currently uploading
    if @_isUploading
      return 'uploading'

    # Everything has been uploaded
    return 'paused'

  insertBlob: (blob, index, preProcessActions, hidden, baseFolder, id) ->
    # Determine the identifier
    id ?= @nextFileId++

    # Create the fileInfo object
    uploadFileItem = new UploadFileItem id, blob, this

    # Mark hidden files
    uploadFileItem.isHidden = true if hidden

    # Files need to be pre-processed
    chain = new Chain()
    chain.chainAction ->
      uploadFileItem.preProcess preProcessActions, baseFolder

    # Add the file into the list of files that need to be uploaded
    chain.chainAction =>
      if !@_uploadToken or uploadFileItem.requiresUpload()
        if (
          uploadFileItem.state is 'scheduledForUpload' or
          uploadFileItem.state is 'uploaded'
        )
          # Hidden files shouldn't be visible in the transfer
          if !hidden
            # Add the file to the 'files' object
            @files[uploadFileItem.id] = uploadFileItem

            # Add the item to the parent folder
            @_addToParentFolder uploadFileItem, baseFolder

          # Insert the file into the upload file list
          if index >= 0
            @_uploadFiles.splice index, 0, uploadFileItem

            # Go back to the index
            @_uploadIndex = index if @_uploadIndex > index
          else
            @_uploadFiles.push uploadFileItem

          # Update the total upload size
          @_totalUploadSize += uploadFileItem.size
          @_totalUploadSize += @_getZipFileOverhead uploadFileItem if (
            !uploadFileItem.originalFile
          )

    # Return the chain (if any)
    chain.chain

  addFiles: (files, baseFolder = @rootFolder) ->
    # Always try to compress files, but don't create thumbnails if encryption is used
    preProcessActions = []

    # Add the file-processor
    preProcessActions.push(@_fileProcessor = new FileProcessor())

    # Add the iOS image renamer (if required)
    preProcessActions.push @_iosImageRenamer ?= new iOSImageRenamer this if (
      iOSImageRenamer.isRequired()
    )

    # Add compression
    preProcessActions.push(
      @_compression ?= new Compression defaultCompressionLevel
    )

    # Add thumbnail generation (disabled for now)
    preProcessActions.push @_thumbNails ?= new Thumbnails this

    # Check if we are adding an array of blobs
    if _isFileArray files
      preProcessPromises = []
      for file in files
        if file.isDirectory
          @_addFolder file, baseFolder
        else
          promise = @insertBlob file, -1, preProcessActions, false, baseFolder
          preProcessPromises.push promise if promise
      return (
        if preProcessPromises.length
          $q.all preProcessPromises
        else
          null
      )
    else
      if file.isDirectory
        @_addFolder files, baseFolder
      else
        return @insertBlob files, -1, preProcessActions, false, baseFolder

  removeItems: (items, removeFromBackend = true) ->
    # Keep track if files have been removed
    # Check if we are deleting an array of blobs
    removeServerFolders = []
    removeServerFiles = []
    removeItem = (item) =>
      if item instanceof FolderItem
        # Remove the folder from the backend
        removeServerFolders.push item
      else
        # Scan through the list of files that is (or will be) uploaded
        removeFromBackEnd = false
        for uploadFileItem, index in @_uploadFiles
          if uploadFileItem is item
            # Remove the file from the upload file list
            @_uploadFiles.splice index, 1

            # Update the total number of bytes that must be uploaded
            @_totalUploadSize -= uploadFileItem.size
            @_totalUploadSize -= @_getZipFileOverhead uploadFileItem if (
              !uploadFileItem.originalFile
            )

            # Check if the file is already (partially) uploaded
            if (
              uploadFileItem.state == 'uploading' or
              uploadFileItem.state == 'uploaded'
            )
              # Remove the file from the backend
              removeFromBackEnd = true

              # Update the number of uploaded bytes
              @uploadedBytes -= uploadFileItem.uploadedBytes

            # Remove all the linked files
            if uploadFileItem.linkedFiles
              for linkedFileInfo in uploadFileItem.linkedFiles
                removeItem linkedFileInfo

            # If the file is a sidecar, then disconnect it form the original file
            uploadFileItem.originalFile?.removeLinkedFile uploadFileItem

            # Reset the upload index, so the next scan will start from the beginning
            @_uploadIndex = 0

        # If the file is already on the backend, then it should be removed
        removeFromBackEnd = true if item.backEndStatus

        # Add the file to the list if it must be removed from the back-end
        if removeFromBackEnd
          removeServerFiles.push item

        # Remove the item from the files array
        delete @files[item.id] unless item.isHidden

      # Remove the item from the folder
      item.folder?.removeItem item

    if _isFileArray items
      for item in items
        removeItem item
    else
      removeItem items

    # Check if we need to remove anything on the server
    if (
      removeFromBackend and
      (removeServerFolders.length || removeServerFiles.length)
    )
      UploadService.removeItems(
        @_uploadToken
        this
        removeServerFolders
        removeServerFiles
      )

    # Return the folders and files that will be deleted from the server
    folders: removeServerFolders
    files: removeServerFiles

  startUpload: ->
    # Add the compression plugin
    @_addAction @_compression if @_compression.compressionLevel > 0

    # Upload started
    @_started = true

    # Add the encryption plugin if a pass-phrase has been set. Otherwise, we'll
    # use a CRC processor to calculate the CRC value (required for ZIP archive)
    action = if @options.aesPassPhrase
      new Encryption @options.aesPassPhrase
    else
      new CRC()
    @_addAction action

    # Set the encryption status
    @status.isEncrypted = !!@options.aesPassPhrase

    # Check if we need to confirm or reconfirm
    if @isNewTransfer
      # Save the confirmation options
      confirmOptions =
        shareMethod: @options.shareMethod
        fromEmail: @options.fromEmail
        message: @options.message
        isEncrypted: @status.isEncrypted

      # Add email options
      if @options.shareMethod is 'email'
        confirmOptions.email =
          recipients:
            recipient.email for recipient in @options.email.recipients
          deleteAfterDownload: @options.email.deleteAfterDownload

    # Wait until all actions are initialized
    $q
      .all(
        (action.initializePromise() for action in @_processActions when (
          action.initializePromise
        ))
      )
      .then =>
        # Start uploading and remember the token
        UploadService.startUpload(this, @options.region, confirmOptions).then (
          token
        ) =>
          # Save the upload token
          @_uploadToken = token

          # Log the upload url
          @shortUrl = token.getId()
          @shareUrl = token.getShareUrl()
          @availableUntil = token.getAvailableUntil()

          # Log short-url
          $log.log(
            "Upload using short-url: #{
              @shortUrl
            } (sent by '#{confirmOptions?.fromEmail or
              '<unknown>'}', bucket: '#{token._internals.serverData.bucket}')"
          )

          # Set the transfer identifier to the proper transfer identifier
          @id = token.getId()

          # Remove all the files that don't need to be uploaded
          deleteItems =
            uploadFileItem for uploadFileItem in @_uploadFiles when (
              not uploadFileItem.requiresUpload()
            )
          @removeItems deleteItems if deleteItems.length

          # Send a request upload event
          UploadListService.requestStart this

  deleteUpload: (deleteFromList) ->
    # Check if the upload has already been started
    if @_uploadToken
      # Cancel the upload
      @_isCancelled = true

      # Delete the upload
      UploadService.deleteUploads [this], deleteFromList

      # Cancel the upload from the list
      UploadListService.finishUpload this

  isUploading: ->
    @_isUploading

  isUploadStarted: ->
    @_startingUploadPromise || !!@_uploadToken

  isUploadCancelled: ->
    !!@_isCancelled

  isUploadFinished: ->
    !!@_isUploadFinished

  isConfirmed: ->
    !@isNewTransfer or @_started

  getTotalUploadSize: ->
    @_totalUploadSize

  getTotalUploadFiles: ->
    @_uploadFiles.length

  removePendingFiles: ->
    # Remove all upload files
    for file in @_uploadFiles
      # Remove file from the files array
      delete @files[file.id]

      # Remove the file from its folder
      file.folder?.removeItem file

    # Reset the upload files array
    @_uploadFiles = []

    # Remove all folders that haven't been uploaded yet
    index = 0
    while index < @folders.length
      folder = @folders[index]
      if folder.state is 'readyForUpload'
        @folders.slice index, 1
      else
        ++index

  raiseProgressEvent: ->
    @_calculateTransferRate true

  getDuplicateFiles: (newFiles, currentFolder = @rootFolder) ->
    duplicateFiles = []
    currentFolderPath = currentFolder.getCompletePath()
    for newFile in newFiles
      if !newFile.isDirectory
        # Determine the folder name
        relativePath = getRelativePath newFile
        if currentFolderPath
          newFile.fullPath = "#{currentFolderPath}/#{relativePath}"
        else
          newFile.fullPath = relativePath

        for key, existingFile of @files
          if (
            newFile.name is existingFile.name and
            newFile.fullPath is existingFile.getCompletePath()
          )
            duplicateFiles.push
              old: existingFile
              new: newFile
              action: if (
                newFile.lastModified >
                existingFile.lastModifiedDate * 1000
              )
                'r'
              else
                'k'
            break
    duplicateFiles

  addFilesWithDuplicateCheck: (fileList, duplicateFiles, baseFolder = @rootFolder) ->
    # Create a copy of the files array (the actual FileList is gone after this function completes)
    if fileList instanceof FileList
      files = []
      files.push file for file in fileList
    else
      files = fileList

    if duplicateFiles.length > 0
      # Update the current list of files
      removeFiles = []
      getUniqueFilename = null
      for file in duplicateFiles
        switch file.action
          when 'k'
            # Remove the new file from the list of added files
            for f, index in files
              if f is file.new
                files.splice index, 1
                break

          when 'r'
            # Remove the old files
            removeFiles.push file.old

          when 'c'
            getUniqueFilename ?= (newFile, folderItem) =>
              # Determine the name (without extension) and extension
              name = newFile.name
              if (extensionIndex = name.indexOf '.') == 0
                extensionIndex = name.substring(1).indexOf '.'
                extensionIndex = extensionIndex + 1 if extensionIndex >= 0
              if extensionIndex > 0
                baseName = name.substring 0, extensionIndex
                extension = name.substring extensionIndex
              else
                baseName = name
                extension = ''

              # Find all the files that start with this prefix and have the same extension
              matchingFiles = []
              for key, file of @files
                # Check if the folder and basename matches
                if (
                  file.folder is folderItem and
                  file.name.indexOf(baseName) == 0
                )
                  # Check if the extension matches (if any)
                  if (
                    !extension or
                    ((ext = file.name.lastIndexOf extension) >= -1 and
                      ext + extension.length == file.name.length)
                  )
                    matchingFiles.push file.name

              # Try an additional number to check if it will be unique
              index = 1
              loop
                tryName = "#{baseName} (#{index})#{extension}"
                foundMatch = false
                for matchFile in matchingFiles
                  foundMatch = true if matchFile is tryName
                if !foundMatch
                  return tryName
                ++index

            # Rename the file, so it's unique
            file.new.targetName = getUniqueFilename file.new, file.old.folder

      # Delete existing files (if any)
      if removeFiles.length > 0
        result = @removeItems removeFiles, false
        @_pendingRemoveItems = (@_pendingRemoveItems || []).concat(
          result.files
        )

    # Add files to the transfer
    if files.length > 0
      @addFiles files, baseFolder

export default Upload
